// S3 Browser functionality
import { S3Client, ListObjectsV2Command, GetObjectCommand, HeadObjectCommand } from '@aws-sdk/client-s3';
import { SSMClient, GetParameterCommand } from '@aws-sdk/client-ssm';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';

const MAX_KEYS = 50; // Items per page - smaller for more dynamic loading

class S3Browser {
    constructor(auth) {
        this.auth = auth;
        this.currentPath = '';
        this.bucketName = null; // Will be fetched from SSM
    }

    // Get S3 client with current credentials
    async getS3Client() {
        const credentials = await this.auth.getAWSCredentials();
        return new S3Client({
            region: 'us-east-1',
            credentials
        });
    }

    // Get SSM client with current credentials
    async getSSMClient() {
        const credentials = await this.auth.getAWSCredentials();
        return new SSMClient({
            region: 'us-east-1',
            credentials
        });
    }

    // Get bucket name from SSM Parameter Store
    async getBucketName() {
        if (this.bucketName) {
            return this.bucketName;
        }

        try {
            const ssmClient = await this.getSSMClient();
            const command = new GetParameterCommand({
                Name: '/meltmedia/s3/bucket-name'
            });
            const response = await ssmClient.send(command);
            this.bucketName = response.Parameter.Value;
            return this.bucketName;
        } catch (error) {
            console.error('Error fetching bucket name from SSM:', error);
            throw new Error('Could not retrieve S3 bucket configuration');
        }
    }

    // List objects in S3 bucket with pagination support
    async listObjects(prefix = '', continuationToken = null) {
        try {
            const s3Client = await this.getS3Client();
            const bucketName = await this.getBucketName();

            const params = {
                Bucket: bucketName,
                Prefix: prefix,
                Delimiter: '/',
                MaxKeys: MAX_KEYS
            };

            if (continuationToken) {
                params.ContinuationToken = continuationToken;
            }

            const command = new ListObjectsV2Command(params);
            const response = await s3Client.send(command);

            // Process the response
            const folders = (response.CommonPrefixes || []).map(item => ({
                type: 'folder',
                name: this.extractFolderName(item.Prefix, prefix),
                path: item.Prefix,
                size: null,
                lastModified: null
            }));

            const files = (response.Contents || [])
                .filter(item => item.Key !== prefix) // Remove the prefix itself if it appears as a file
                .map(item => ({
                    type: 'file',
                    name: this.extractFileName(item.Key, prefix),
                    path: item.Key,
                    size: item.Size,
                    lastModified: item.LastModified,
                    extension: this.getFileExtension(item.Key)
                }));

            return {
                folders,
                files,
                isTruncated: response.IsTruncated || false,
                nextContinuationToken: response.NextContinuationToken || null,
                totalCount: folders.length + files.length
            };
        } catch (error) {
            console.error('Error listing S3 objects:', error);
            throw error;
        }
    }

    // Generate signed URL for viewing files
    async getSignedUrl(key, expiresIn = 3600) {
        try {
            const s3Client = await this.getS3Client();
            const bucketName = await this.getBucketName();

            const command = new GetObjectCommand({
                Bucket: bucketName,
                Key: key
            });

            const signedUrl = await getSignedUrl(s3Client, command, { expiresIn });
            return signedUrl;
        } catch (error) {
            console.error('Error generating signed URL:', error);
            throw error;
        }
    }

    // Extract folder name from full prefix path
    extractFolderName(fullPrefix, currentPrefix) {
        const relativePath = fullPrefix.substring(currentPrefix.length);
        return relativePath.replace(/\/$/, ''); // Remove trailing slash
    }

    // Extract file name from full key path
    extractFileName(fullKey, currentPrefix) {
        return fullKey.substring(currentPrefix.length);
    }

    // Get file extension
    getFileExtension(filename) {
        const lastDot = filename.lastIndexOf('.');
        return lastDot > -1 ? filename.substring(lastDot + 1).toLowerCase() : '';
    }

    // Get file type based on extension
    getFileType(extension) {
        const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
        const videoExts = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'];
        const audioExts = ['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'];

        if (imageExts.includes(extension)) return 'image';
        if (videoExts.includes(extension)) return 'video';
        if (audioExts.includes(extension)) return 'audio';
        return 'file';
    }

    // Format file size
    formatFileSize(bytes) {
        if (!bytes) return '—';

        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    }

    // Get file icon based on type
    getFileIcon(type) {
        switch (type) {
            case 'folder': return '📁';
            case 'image': return '🖼️';
            case 'video': return '🎬';
            case 'audio': return '🎵';
            default: return '📄';
        }
    }

    // Parse path components for breadcrumb navigation
    parsePath(path) {
        if (!path || path === '/') return [];

        const parts = path.replace(/^\/+|\/+$/g, '').split('/');
        const breadcrumbs = [];
        let currentPath = '';

        parts.forEach(part => {
            currentPath += part + '/';
            breadcrumbs.push({
                name: part,
                path: currentPath
            });
        });

        return breadcrumbs;
    }

    // Navigate to a new path - load all items to avoid folder/file mixing
    async navigateTo(path) {
        this.currentPath = path;

        // Load all items for proper sorting
        return await this.loadAllItems(path);
    }

    // Load all items in a directory to avoid pagination mixing folders and files
    async loadAllItems(prefix = '') {
        const allFolders = [];
        const allFiles = [];
        let continuationToken = null;

        do {
            const result = await this.listObjects(prefix, continuationToken);
            allFolders.push(...result.folders);
            allFiles.push(...result.files);
            continuationToken = result.nextContinuationToken;
        } while (continuationToken);

        // Sort items appropriately
        allFolders.sort((a, b) => a.name.localeCompare(b.name));
        allFiles.sort((a, b) => a.name.localeCompare(b.name));

        return {
            folders: allFolders,
            files: allFiles,
            isTruncated: false,
            nextContinuationToken: null,
            totalCount: allFolders.length + allFiles.length
        };
    }

    // Get file metadata from S3
    async getFileMetadata(key) {
        try {
            const s3Client = await this.getS3Client();
            const bucketName = await this.getBucketName();
            const params = {
                Bucket: bucketName,
                Key: key
            };

            console.log('Getting metadata for:', key);
            const command = new HeadObjectCommand(params);
            const response = await s3Client.send(command);

            return {
                contentLength: response.ContentLength,
                lastModified: response.LastModified,
                contentType: response.ContentType,
                etag: response.ETag
            };

        } catch (error) {
            console.error('Error getting file metadata:', error);
            throw error;
        }
    }

}

export default S3Browser;